import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Brain, X, ChevronRight, FileText, Bot, Network } from "lucide-react";
import { api } from "../../lib/api";
import DossierPicker from "../warroom/DossierPicker";
import { toast } from "sonner";

/**
 * MindChangedEditor — pin ONE archive entry that genuinely changed your mind.
 *
 * Accepts ALL three archive kinds:
 *   • dossier      — curated file
 *   • ai_dossier   — published AI deep dive
 *   • connection   — published Connect-the-Dots map
 *
 * The backend stores a unified `mind_changed_ref: {kind, ref}` blob and
 * hydrates the right collection on read. Legacy `mind_changed_dossier_slug`
 * is still honoured for back-compat but new pins use the generic ref.
 */
const KIND_META = {
  dossier:    { Icon: FileText, color: "#ff3b30", label: "DOSSIER" },
  ai_dossier: { Icon: Bot,      color: "#a78bfa", label: "AI DEEP DIVE" },
  connection: { Icon: Network,  color: "#f472b6", label: "CONNECTION" },
};

// DossierPicker returns `type` using the same set as the backend. Defensive
// normalize in case anything ever returns a near-miss value.
const normalizeKind = (t) => {
  const k = (t || "").toLowerCase();
  if (k === "dossier" || k === "curated") return "dossier";
  if (k === "ai_dossier" || k === "ai" || k === "deep_dive") return "ai_dossier";
  if (k === "connection" || k === "connect_dots") return "connection";
  return null;
};

export default function MindChangedEditor({ mindChanged, onSaved }) {
  const [picking, setPicking] = useState(false);

  const save = async (ref) => {
    try {
      // Empty ref → clear pin. Non-empty → send the generic blob.
      const payload = ref ? { mind_changed_ref: ref } : { mind_changed_ref: { kind: "", ref: "" } };
      await api.put("/me/profile", payload);
      toast.success(ref ? "Pinned a mind-changer" : "Cleared");
      onSaved?.(ref || null);
      setPicking(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    }
  };

  const meta = (mindChanged && KIND_META[mindChanged.kind]) || KIND_META.dossier;
  const Icon = meta.Icon;
  // Older hydration shape used `slug` (curated only). New shape uses `url`.
  // Fall back gracefully.
  const targetUrl = mindChanged?.url
    || (mindChanged?.slug ? `/dossier/${mindChanged.slug}` : "#");

  return (
    <div className="border border-[#22d3ee]/40 bg-[#0a0a0a] bg-gradient-to-br from-[#22d3ee]/[0.08] to-[#22d3ee]/[0.02] p-4" data-testid="mind-changed-editor">
      <div className="flex items-center justify-between mb-3">
        <div className="font-mono text-[10px] tracking-[0.25em] text-zinc-500 flex items-center gap-2">
          <Brain size={11} /> THIS CHANGED MY MIND
        </div>
        <button
          type="button"
          onClick={() => setPicking((v) => !v)}
          data-testid="mind-changed-pick-btn"
          className="font-mono text-[10px] tracking-[0.2em] text-[#ffd60a] hover:text-white transition-colors"
        >
          {mindChanged ? "CHANGE" : "PICK"}
        </button>
      </div>

      {mindChanged && !picking && (
        <Link
          to={targetUrl}
          data-testid="mind-changed-display"
          className="flex items-start gap-3 border border-[#22d3ee]/30 bg-[#22d3ee]/[0.04] p-3 hover:border-[#22d3ee]/60 hover:bg-[#22d3ee]/[0.08] transition-colors group"
        >
          <div className="font-heading text-3xl text-zinc-700 group-hover:text-white shrink-0">"</div>
          <div className="flex-1 min-w-0">
            <div className="font-heading text-base text-white leading-tight">{mindChanged.title}</div>
            <div className="font-mono text-[9px] tracking-[0.2em] mt-1 uppercase truncate flex items-center gap-1.5">
              <Icon size={10} style={{ color: meta.color }} />
              <span style={{ color: meta.color }}>{meta.label}</span>
              {mindChanged.category && <span className="text-zinc-500"> · {mindChanged.category}</span>}
              {mindChanged.credibility && (
                <span className="text-zinc-500"> · {(mindChanged.credibility || "").toUpperCase()}</span>
              )}
            </div>
            {mindChanged.summary && (
              <p className="text-xs text-zinc-400 mt-2 line-clamp-2 italic">"{mindChanged.summary}"</p>
            )}
          </div>
          <ChevronRight size={14} className="text-zinc-600 group-hover:text-white shrink-0 mt-1" />
        </Link>
      )}

      {!mindChanged && !picking && (
        <p className="text-sm text-zinc-500">
          Pick anything from the archive — a curated file, an AI deep dive, or a connection map — where you genuinely shifted your view. Wear it publicly.
        </p>
      )}

      {picking && (
        <div className="space-y-2">
          <DossierPicker
            onPick={(d) => {
              const kind = normalizeKind(d.type);
              if (!kind) {
                toast.error("Pick a dossier, AI deep dive, or connection map");
                return;
              }
              // The picker returns `slug` for dossiers and ids for AI/conn —
              // either way it's the canonical ref the backend expects.
              save({ kind, ref: d.slug });
            }}
          />
          <div className="flex items-center gap-2">
            {mindChanged && (
              <button
                type="button"
                onClick={() => save("")}
                data-testid="mind-changed-clear-btn"
                className="inline-flex items-center gap-1 font-mono text-[10px] tracking-[0.2em] text-zinc-500 hover:text-[#ff3b30] transition-colors"
              >
                <X size={10} /> CLEAR PIN
              </button>
            )}
            <button
              type="button"
              onClick={() => setPicking(false)}
              data-testid="mind-changed-cancel-btn"
              className="ml-auto font-mono text-[10px] tracking-[0.2em] text-zinc-500 hover:text-white transition-colors"
            >
              CANCEL
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
